/* Revue des doublons Annuaire — paires côte à côte, fusion en 1 clic. */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Copy, X, Loader2, ArrowLeft, ArrowRight, Users2, CheckCircle2, SkipForward } from "lucide-react";

export default function DedupReview({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [pairs, setPairs] = useState(null);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/workspace/annuaire/dedup/pairs");
      setPairs(r.data.pairs || []);
      setIdx(0);
    } catch (e) { /* silencieux */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = pairs?.length ?? 0;
  if (pairs !== null && total === 0 && !open) return null;

  const pair = pairs?.[idx];

  const advance = async () => {
    await load();
    onChanged?.();
  };

  const merge = async (keepId, removeId) => {
    setBusy(true);
    try {
      await api.post("/workspace/annuaire/dedup/merge", { keep_id: keepId, remove_id: removeId });
      notify.success("Fusion effectuée.");
      await advance();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const keepBoth = async () => {
    setBusy(true);
    try {
      await api.post("/workspace/annuaire/dedup/keep-both", { id_a: pair.a.id, id_b: pair.b.id });
      notify.success("Paire conservée (2 acteurs distincts).");
      await advance();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-ghost text-xs relative"
        data-testid="annuaire-dedup-btn"
        title="Revue des doublons probables"
      >
        <Copy className="w-3.5 h-3.5 text-[#EF9F27]" /> Doublons
        {total > 0 && (
          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF9F27]/20 text-[#EF9F27] font-semibold">{total}</span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)} data-testid="dedup-modal">
          <div className="bg-[#14161c] border border-white/10 rounded-lg p-5 w-[760px] max-w-[95%] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Copy className="w-4 h-4 text-[#EF9F27]" /> Revue des doublons
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {total === 0 ? "Tout est arbitré 🎉" : `Paire ${idx + 1} / ${total} — même nom à 1-2 caractères près`}
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" data-testid="dedup-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {total === 0 ? (
              <div className="py-10 text-center text-sm text-zinc-400">
                <CheckCircle2 className="w-8 h-8 text-[#1D9E75] mx-auto mb-2" />
                Plus aucun doublon probable dans l'Annuaire.
              </div>
            ) : pair ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {[pair.a, pair.b].map((e, i) => (
                    <div key={e.id} className="border border-white/10 rounded-lg p-3" data-testid={`dedup-card-${i === 0 ? "a" : "b"}`}>
                      <div className="text-sm font-semibold">{e.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{e.type} · {e.status} · {e.sector || "—"}</div>
                      <ul className="text-[11px] text-zinc-400 mt-2 space-y-1">
                        <li><Users2 className="w-3 h-3 inline mr-1" />{e.contacts_count} contact(s) · {e.children_count} sous-entité(s)</li>
                        {e.parent_name && <li>Parent : {e.parent_name}</li>}
                        {e.description && <li className="text-zinc-500">{e.description}</li>}
                        {(e.tags || []).length > 0 && <li className="text-zinc-600">{e.tags.join(", ")}</li>}
                      </ul>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => merge(e.id, i === 0 ? pair.b.id : pair.a.id)}
                        className="btn-primary text-[11px] w-full justify-center mt-3"
                        data-testid={`dedup-merge-keep-${i === 0 ? "a" : "b"}`}
                      >
                        {i === 0 ? <ArrowLeft className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                        Garder celui-ci (fusionner l'autre dedans)
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={keepBoth}
                    className="btn-secondary text-xs"
                    data-testid="dedup-keep-both"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Ce sont 2 acteurs distincts — garder les deux
                  </button>
                  <button
                    type="button"
                    disabled={busy || idx >= total - 1}
                    onClick={() => setIdx((i) => Math.min(i + 1, total - 1))}
                    className="btn-ghost text-xs"
                    data-testid="dedup-skip"
                  >
                    <SkipForward className="w-3.5 h-3.5" /> Passer
                  </button>
                </div>
                {busy && <div className="mt-2 text-[11px] text-zinc-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Traitement…</div>}
                <p className="text-[10px] text-zinc-600 mt-3">
                  Fusion : les contacts, tags et champs manquants de l'autre fiche sont récupérés, ses sous-entités sont rattachées à la fiche conservée, puis elle est supprimée.
                </p>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
